// components/TicketDetailsModal.tsx
import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { X, User, MessageCircle, Paperclip, Download, Send, Calendar, Upload, ExternalLink } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import Button from "../../components/ui/Button";
import Spinner from "../../modules/shared/components/Spinner";
import { useAppDispatch, createChat, UpdateUserChats, createConversation, getUserConversations } from "../../redux";
import { uploadContractFile } from "../../redux/Slices/contractSlice";
import { RoleEnum, ContextEnum, NotificationStatusEnum, NotificationTypeEnum, NotificationPriorityEnum } from "../../modules/shared/model";
import { useTranslation } from "react-i18next";
import { useWebSocket } from "../../hooks/useWebSocket";
import { createUserNotificationAction } from "../../redux/Actions/UserNotificationActions";
import { useSelector } from "react-redux";

interface Attachment {
    id: string;
    name: string;
    size: string;
    url?: string; // Add this for image URLs
    type?: string; // Add this to distinguish between file types
}

interface Message {
    id: number;
    sender: string;
    role: string;
    message: string;
    timestamp: string;
    attachments: Attachment[];
}

interface Ticket {
    id: string;
    subject: string;
    description: string;
    status: number;
    priority: number;
    category: number;
    createdAt: string;
    updatedAt: string;
    messages: Message[];
    assignee: string;
    department: string;
}

interface TicketDetailsModalProps {
    ticket: Ticket;
    onClose: () => void;
    newMessage: string;
    setNewMessage: (message: string) => void;
    onSendMessage: () => void;
    getStatusColor: (statusId: number) => string;
    getPriorityColor: (priorityId: number) => string;
    getStatusName: (statusId: number) => string;
    getPriorityName: (priorityId: number) => string;
    getCategoryName: (categoryId: number) => string;
    formatDate: (dateString: string) => string;
    attachments: any;
    setAttachments: any;
    getRoleName:any;
    conversations: any[]; // Add conversations to props
    user: any;
}

const TicketDetailsModal: React.FC<TicketDetailsModalProps> = ({
    ticket,
    onClose,
    newMessage,
    setNewMessage,
    onSendMessage,
    getStatusColor,
    getPriorityColor,
    getStatusName,
    getPriorityName,
    getCategoryName,
    formatDate,
    attachments,
    setAttachments,
    getRoleName,
    conversations,
    user,
}: any) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { chats } = useSelector((state: any) => state.auth);
    const [isUploading, setIsUploading] = useState(false);
    const [fileInputKey, setFileInputKey] = useState(Date.now());
    const [messages, setMessages] = useState<Message[]>(ticket.messages || []);
    
    // Ref for auto-scrolling
    const messagesEndRef = React.useRef<HTMLDivElement>(null);
    const messagesContainerRef = React.useRef<HTMLDivElement>(null);

    // WebSocket message handler
    const handleWebSocketMessage = useCallback((message: any) => {
        // Handle both message types for compatibility
        if ((message.type === "new_support_message" || message.type === "new_conversation") && 
            message.detail?.ticketId === ticket.id) {
            // Refresh conversations to get the latest messages
            if (chats?.id) {
                dispatch(getUserConversations({ id: chats.id })).catch((err) => {
                    console.error("Failed to refresh conversations:", err);
                });
            }
        }
    }, [ticket.id, chats?.id, dispatch]);

    // WebSocket for real-time messaging
    const { isConnected, sendMessage: sendWebSocketMessage } = useWebSocket({
        onMessage: handleWebSocketMessage
    });

    useEffect(() => {
        if (conversations && conversations.length > 0) {
            const processedMessages = conversations.map(conv => {
                const isClient = conv.sender?.id === user?.id;
                const senderName = isClient ? `${user?.firstName} ${user?.lastName}` : "Support Team";

                // Properly process attachments with type detection
                const mappedAttachments =
                    conv.attachments?.map(att => {
                        const extension = att.path.split(".").pop()?.toLowerCase();
                        const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(extension || "");

                        return {
                            id: att.id,
                            name: att.path.split("/").pop() || "attachment",
                            size: "Unknown",
                            url: att.path,
                            type: isImage ? "image" : "file",
                        };
                    }) || [];

                return {
                    id: conv.id,
                    sender: senderName,
                    role: isClient ? getRoleName(RoleEnum.client) : "support",
                    message:
                        conv.message ||
                        (mappedAttachments.length > 0 ? t("ticketDetailsModal.sentAttachments", { count: mappedAttachments.length }) : ""),
                    timestamp: conv.createdAt,
                    attachments: mappedAttachments,
                };
            });

            setMessages(
                processedMessages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
            );
        } else {
            setMessages(ticket.messages || []);
        }
    }, [conversations, user, ticket.messages]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setIsUploading(true);

        try {
            const newAttachments: Attachment[] = [];

            for (const file of Array.from(files)) {
                const { payload } = await dispatch(uploadContractFile(file) as any);
                const fileId = payload?.file?.id;
                const fileUrl = payload?.file?.path; // Assuming your API returns the URL

                if (fileId) {
                    newAttachments.push({
                        id: fileId,
                        name: file.name,
                        size: `${(file.size / 1024).toFixed(1)} KB`,
                        type: file.type,
                        url: fileUrl || URL.createObjectURL(file), // Fallback to object URL if no server URL
                    });
                }
            }

            setAttachments(prev => [...prev, ...newAttachments]);
        } finally {
            setIsUploading(false);
            setFileInputKey(Date.now());
        }
    };

    const handleRemoveAttachment = (id: string) => {
        setAttachments(prev => prev.filter(attachment => attachment.id !== id));
    };

    const handleSendWithAttachments = async () => {
        if (newMessage.trim() || attachments.length > 0) {
            try {

                // Create or update chat
                const chatPayload = {
                    unreadCount: {},
                    participants: [{ id: user?.id }],
                    contextId: ticket.id,
                    contextType: { id: ContextEnum.Support_Ticket },
                    lastMessageAt: new Date().toISOString(),
                    lastMessage: newMessage || t("ticketDetailsModal.sentAttachments", { count: attachments.length }),
                };

                let chatResponse;
                if (chats) {
                    chatResponse = await dispatch(UpdateUserChats({ id: chats.id, data: chatPayload })).unwrap();
                } else {
                    chatResponse = await dispatch(createChat({ data: chatPayload })).unwrap();
                }

                // Create conversation with attachments
                const conversationPayload = {
                    isRead: false,
                    message: newMessage,
                    sender: { id: user?.id },
                    chatId: { id: chatResponse.id },
                    attachments: attachments.map(att => ({ id: att.id })),
                };

                const conversation = await dispatch(createConversation({ data: conversationPayload })).unwrap();

                // Immediately refresh conversations to show the new message
                await dispatch(getUserConversations({ id: chatResponse.id }));

                // Send WebSocket notification to support team (non-blocking)
                setTimeout(async () => {
                    try {
                        if (sendWebSocketMessage && isConnected) {
                            const messageData = {
                                receivers: ["admin"],
                                sender: user?.id || "",
                                type: "new_support_message",
                                detail: {
                                    conversationId: conversation.id,
                                    chatId: chatResponse.id,
                                    message: conversationPayload.message || t("ticketDetailsModal.sentAttachments", { count: attachments.length }),
                                    senderId: user?.id,
                                    ticketId: ticket.id,
                                    attachments: conversationPayload.attachments,
                                    timestamp: new Date().toISOString()
                                }
                            };
                            
                            await sendWebSocketMessage(messageData);
                        }
                    } catch (wsError) {
                        console.error("WebSocket error:", wsError);
                    }
                }, 100);

                setNewMessage("");
                setAttachments([]);
            } catch (error) {
                console.error("Error sending message:", error);
            }
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-xl w-full max-w-4xl mx-4 h-[90vh] flex flex-col overflow-hidden">
                {/* Modal Header */}
                <div className="p-6 border-b flex justify-between items-start">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <h2 className="text-xl font-semibold">{ticket.subject}</h2>
                            <Badge variant={undefined}>{ticket.id}</Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                            <Badge variant={undefined} className={getStatusColor(Number(ticket.status))}>
                                {getStatusName(ticket.status)}
                            </Badge>
                            <Badge variant={undefined} className={getPriorityColor(Number(ticket.priority))}>
                                {getPriorityName(ticket.priority)} {t("ticketDetailsModal.priority")}
                            </Badge>
                            <span className="text-sm text-gray-500 flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatDate(ticket.createdAt)}
                            </span>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Ticket Details */}
                <div className="flex-1 overflow-auto">
                    {/* Ticket Information */}
                    <div className="p-6 bg-gray-50 border-b">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <h3 className="text-sm font-medium text-gray-500 mb-1">{t("ticketDetailsModal.department")}</h3>
                                <p className="text-gray-900">{ticket.department}</p>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-gray-500 mb-1">{t("ticketDetailsModal.assignee")}</h3>
                                <p className="text-gray-900">{ticket.assignee}</p>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-gray-500 mb-1">{t("ticketDetailsModal.category")}</h3>
                                <p className="text-gray-900">{getCategoryName(ticket.category)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Message History */}
                    <div ref={messagesContainerRef} className="p-6 space-y-6 overflow-y-auto max-h-[50vh]">
                        {messages.map(message => (
                            <div
                                key={message.id}
                                className={`flex gap-4 ${message.role === getRoleName(RoleEnum.client) ? "flex-row-reverse" : ""}`}>
                                <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center">
                                    {message.role === getRoleName(RoleEnum.client) ? (
                                        <User className="w-5 h-5 text-gray-600" />
                                    ) : (
                                        <MessageCircle className="w-5 h-5 text-gray-600" />
                                    )}
                                </div>
                                <div className={`flex-1 max-w-2xl ${message.role === getRoleName(RoleEnum.client) ? "text-right" : ""}`}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-medium text-gray-900">{message.sender}</span>
                                        <span className="text-sm text-gray-500">{formatDate(message.timestamp)}</span>
                                    </div>
                                    <div
                                        className={`p-4 rounded-lg ${
                                            message.role === getRoleName(RoleEnum.client)
                                                ? "bg-blue-50 text-blue-900"
                                                : "bg-gray-100 text-gray-900"
                                        }`}>
                                        {message.message && <p className="mb-2">{message.message}</p>}

                                        {message.attachments.length > 0 && (
                                            <div className="mt-3 space-y-2">
                                                {message.attachments.map((attachment, index) => (
                                                    <div key={index} className="flex flex-col gap-2">
                                                        {/* Image display */}
                                                        {attachment.type === "image" && attachment.url && (
                                                            <div className="max-w-xs rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                                                                <img
                                                                    src={attachment.url}
                                                                    alt={attachment.name}
                                                                    className="w-full h-auto max-h-60 object-contain cursor-pointer"
                                                                    onClick={() =>
                                                                        window.open(attachment.url, "_blank")
                                                                    }
                                                                />
                                                            </div>
                                                        )}

                                                        {/* File info - shown for all attachments */}
                                                        <div
                                                            className={`flex items-center gap-2 text-sm p-2 rounded ${
                                                                attachment.type === "image"
                                                                    ? "bg-gray-50 mt-1"
                                                                    : "bg-white"
                                                            }`}>
                                                            <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                            <span className="truncate">{attachment.name}</span>
                                                            <span className="text-gray-400 text-xs whitespace-nowrap">
                                                                ({attachment.size})
                                                            </span>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="ml-auto p-1"
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    window.open(attachment.url, "_blank");
                                                                }}>
                                                                <ExternalLink className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                </div>

                {/* Reply Section */}
                <div className="p-6 border-t bg-white">
                    {/* Attachment Preview */}
                    {attachments.length > 0 && (
                        <div className="mb-4 space-y-2">
                            {attachments.map(attachment => (
                                <div
                                    key={attachment.id}
                                    className="flex items-center gap-2 text-sm p-2 bg-gray-50 rounded">
                                    <Paperclip className="w-4 h-4 text-gray-400" />
                                    <span className="flex-1">{attachment.name}</span>
                                    <span className="text-gray-400">({attachment.size})</span>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveAttachment(attachment.id)}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex gap-4">
                        <textarea
                            value={newMessage}
                            onChange={e => setNewMessage(e.target.value)}
                            placeholder={t("ticketDetailsModal.typeYourMessage")}
                            className="flex-1 p-3 border rounded-lg resize-none"
                            rows={3}
                        />
                        <div className="space-y-2">
                            <Button
                                className="w-full"
                                disabled={!newMessage.trim() && attachments.length === 0}
                                onClick={handleSendWithAttachments}>
                                <Send className="w-4 h-4 mr-2" />
                                {t("ticketDetailsModal.send")}
                            </Button>
                            <div className="relative">
                                <Button variant="outline" className="w-full">
                                    {isUploading ? (
                                        <>
                                            <Spinner size={4} />
                                            {t("ticketDetailsModal.uploading")}
                                        </>
                                    ) : (
                                        <>
                                            <Paperclip className="w-4 h-4 mr-2" />
                                            {t("ticketDetailsModal.attach")}
                                        </>
                                    )}
                                </Button>
                                <input
                                    key={fileInputKey}
                                    type="file"
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    multiple
                                    onChange={handleFileUpload}
                                    disabled={isUploading}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default TicketDetailsModal;
